import { getLocalTimeZone } from "@internationalized/date";
import { CheckmarkFill18Icon } from "@vygruppen/spor-icon-react";
import { startOfDay } from "date-fns";
import { ProductionDeviationForm } from "features/CenterContent/RoleContent/ProductionDeviation/types/formSchema";
import { useTrainSeriesRange } from "features/CenterContent/RoleContent/ProductionDeviation/useTrainSeriesRange";
import { sortTrainSeries } from "features/CenterContent/RoleContent/ProductionDeviation/utils";
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import {
  DropsStaticAlert,
  FilterChip,
  Flex,
  Switch,
  Text,
} from "shared/components/Spor";
import { toggle } from "shared/utils/objectUtils";
import styled, { useTheme } from "styled-components";

type DerivedTrainSeriesProps = {
  trainSeries: string[];
  setTrainSeries: Dispatch<SetStateAction<string[]>>;
  includeAll: boolean;
  setIncludeAll: Dispatch<SetStateAction<boolean>>;
};

const SeriesContainer = styled(Flex)`
  padding: 3px;
`;

export const DerivedTrainSeries: FC<DerivedTrainSeriesProps> = ({
  trainSeries,
  setTrainSeries,
  includeAll,
  setIncludeAll,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    watch,
    formState: { errors },
  } = useFormContext<ProductionDeviationForm>();

  const durations = watch("durations");
  const fromTime = startOfDay(durations.from_time.toDate(getLocalTimeZone()));
  const toTime = startOfDay(durations.to_time.toDate(getLocalTimeZone()));

  const hasDurationsError = !!(
    errors.durations?.from_time || errors.durations?.to_time
  );

  const { data, status } = useTrainSeriesRange(
    fromTime,
    toTime,
    !hasDurationsError,
    includeAll,
  );

  const availableSeries = useMemo(() => {
    if (!data) return [];
    return sortTrainSeries(data.trainSeries);
  }, [data]);
  const availableSeriesKey = availableSeries.join(",");

  // When the derived set changes (e.g. after changing dates), drop any selected series no longer available.
  const prevAvailableSeriesKey = useRef<string | null>(null);
  useEffect(() => {
    if (availableSeriesKey === prevAvailableSeriesKey.current) return;
    prevAvailableSeriesKey.current = availableSeriesKey;
    setTrainSeries((current) =>
      current.filter((series) => availableSeries.includes(series)),
    );
  }, [availableSeriesKey, availableSeries, setTrainSeries]);

  return (
    <Flex flexDir="column" gap="6px" w="100%">
      <Text variant="sm" fontWeight={700} color={theme.colorTextMain}>
        {t("Berørte togserier")}
      </Text>
      <Flex gap="6px" alignItems="center">
        <Switch
          size="sm"
          checked={includeAll}
          onCheckedChange={() => setIncludeAll((current) => !current)}
        />
        <Text variant="xs" color={theme.colorTextSecondary}>
          {t("Se alle togserier")}
        </Text>
      </Flex>
      <SeriesContainer flexDir="column" w="100%">
        {hasDurationsError && (
          <DropsStaticAlert variant="info" width="100%">
            {t("Velg en gyldig tidsperiode for å se tilgjengelige togserier")}
          </DropsStaticAlert>
        )}
        {!hasDurationsError && status === "pending" && (
          <SkeletonLoader skeletonType="selectTrains" />
        )}
        {!hasDurationsError && status === "error" && (
          <FailureMessage flex={1} m="0px" maxW="100%" />
        )}
        {!hasDurationsError &&
          status === "success" &&
          availableSeries.length === 0 && (
            <DropsStaticAlert variant="info" width="100%">
              {t("Ingen tog i valgt periode")}
            </DropsStaticAlert>
          )}
        {!hasDurationsError &&
          status === "success" &&
          availableSeries.length > 0 && (
            <Flex w="100%" gap="6px" flexWrap="wrap">
              {availableSeries.map((series) => (
                <FilterChip
                  key={series}
                  size="xs"
                  icon={{ default: null, checked: <CheckmarkFill18Icon /> }}
                  checked={trainSeries.includes(series)}
                  onCheckedChange={() =>
                    setTrainSeries(sortTrainSeries(toggle(trainSeries, series)))
                  }
                >
                  <Text variant="xs">{series}</Text>
                </FilterChip>
              ))}
            </Flex>
          )}
      </SeriesContainer>
    </Flex>
  );
};
